import { createBossWaveQueue, isBossWave } from '../entities/boss.js';

let waveState = null;
const biomes = ['alley', 'plaza', 'parkingDeck'];
const phaseDurations = { lull: 4, buildup: 9, peak: 22, reward: 4 };

export function initWaveDirector(game, options = {}) {
  waveState = {
    game,
    spawnInterval: options.spawnInterval ?? 0.32,
    spawnTimer: 0,
    waveDelay: 0,
    phase: 'lull',
    phaseTime: 0,
  };
  if (!game.spawnQueue?.length) startWave(game.wave || 1);
  return waveState;
}

export function startWave(wave = waveState?.game?.wave || 1) {
  const game = waveState.game;
  game.wave = wave;
  game.boss = null;
  game.biome = biomes[(wave - 1) % biomes.length];
  game.mapTier = biomes.indexOf(game.biome);
  game.pacingPhase = 'lull';
  game.spawnQueue = createBossWaveQueue(wave, game.mapTier || 0);
  if (game.spawnMultiplier > 1) {
    game.spawnQueue = game.spawnQueue.flatMap((spec) => spec.boss ? [spec] : [spec, { ...spec }]);
  }
  const mercyStacks = game.upgrades?.mercy || 0;
  const intro = wave === 1 ? 2.6 : isBossWave(wave) ? 3 : 1.5;
  game.waveIntroTimer = intro;
  game.mercyTimer = Math.max(game.mercyTimer || 0, (wave === 1 ? 4.2 : isBossWave(wave) ? 3.2 : 2) + mercyStacks * 0.65);
  waveState.spawnTimer = intro;
  waveState.waveDelay = 0;
  waveState.phase = 'lull';
  waveState.phaseTime = 0;
  return game.spawnQueue;
}

export function updateWaveDirector(dt, options = {}) {
  if (!waveState) return;
  const { game } = waveState;
  if (game.mode && game.mode !== 'playing') return;

  const {
    player,
    enemies = [],
    spawnEnemy,
    onWaveStart = null,
    onBossWave = null,
  } = options;

  tickMercy(dt, game);
  if (game.waveIntroTimer > 0) {
    game.waveIntroTimer = Math.max(0, game.waveIntroTimer - dt);
    game.pacingPhase = isBossWave(game.wave) ? 'priority' : 'incoming';
    return;
  }

  updatePacing(dt, game);

  if (!game.spawnQueue.length && enemies.length === 0) {
    waveState.waveDelay += dt;
    game.pacingPhase = 'reward';
    if (waveState.waveDelay >= phaseDurations.reward) {
      startWave((game.wave || 1) + 1);
      onWaveStart?.(game.wave);
      if (isBossWave(game.wave)) onBossWave?.(game.wave);
    }
    return;
  }

  waveState.spawnTimer -= dt;
  if (game.spawnQueue.length && waveState.spawnTimer <= 0 && spawnEnemy) {
    const spec = game.spawnQueue.shift();
    spawnEnemy(spec, { player, game });
    waveState.spawnTimer = spec.boss ? 1.2 : spawnIntervalForPhase(waveState.phase, waveState.spawnInterval) / Math.min(1.32, game.sectorThreat || 1);
  }
}

function updatePacing(dt, game) {
  waveState.phaseTime += dt;
  if (waveState.phase === 'lull' && waveState.phaseTime >= phaseDurations.lull) setPhase('buildup');
  if (waveState.phase === 'buildup' && waveState.phaseTime >= phaseDurations.buildup) setPhase('peak');
  if (waveState.phase === 'peak' && waveState.phaseTime >= phaseDurations.peak) setPhase('reward');
  game.pacingPhase = waveState.phase;
}

function setPhase(phase) {
  waveState.phase = phase;
  waveState.phaseTime = 0;
}

function spawnIntervalForPhase(phase, base) {
  if (phase === 'lull') return base * 1.2;
  if (phase === 'buildup') return base * 0.72;
  if (phase === 'peak') return base * 0.28;
  return base * 1.35;
}

function tickMercy(dt, game) {
  if (game.mercyTimer > 0) game.mercyTimer = Math.max(0, game.mercyTimer - dt);
}
